import React from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { BlurhashImage } from "./blurhash-image";
import { cn } from "@/lib/utils";

interface ImageDisplayProps {
  imageKey: string;
  blurhash?: string;
  alt?: string;
  className?: string;
  fallback?: React.ReactNode;
  expiresIn?: number;
  width?: number;
  height?: number;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  fill?: boolean;
}

export function ImageDisplay({
  imageKey,
  blurhash,
  alt = "Image",
  className,
  fallback,
  expiresIn = 60 * 60 * 24, // 24 hours default
  width,
  height,
  objectFit = "cover",
  fill = false,
}: ImageDisplayProps) {
  const imageUrl = useQuery(api.r2.getImageUrl, {
    key: imageKey,
    expiresIn,
  });

  if (!imageUrl) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-muted rounded-lg",
          className
        )}
        style={{ width, height }}
      >
        {fallback || (
          <div className="animate-pulse bg-muted-foreground/20 rounded w-full h-full"></div>
        )}
      </div>
    );
  }

  if (fill) {
    return (
      <BlurhashImage
        src={imageUrl}
        blurhash={blurhash}
        alt={alt}
        className={cn("absolute inset-0", className)}
        width={undefined}
        height={undefined}
        objectFit={objectFit}
      />
    );
  }

  return (
    <BlurhashImage
      src={imageUrl}
      blurhash={blurhash}
      alt={alt}
      className={className}
      width={width}
      height={height}
      objectFit={objectFit}
    />
  );
}

interface ImageGalleryProps {
  images: Array<{
    key: string;
    blurhash?: string;
    filename?: string;
  }>;
  className?: string;
  imageClassName?: string;
  expiresIn?: number;
  onImageClick?: (key: string, index: number) => void;
}

export function ImageGallery({
  images,
  className,
  imageClassName,
  expiresIn = 60 * 60 * 24,
  onImageClick,
}: ImageGalleryProps) {
  const imageKeys = images.map((img) => img.key);
  const imageUrls = useQuery(
    api.r2.getImageUrls,
    imageKeys.length > 0
      ? {
          keys: imageKeys,
          expiresIn,
        }
      : "skip"
  );

  if (!imageUrls || imageUrls.length === 0) {
    return (
      <div className={cn("grid grid-cols-2 md:grid-cols-3 gap-4", className)}>
        {images.map((image, index) => (
          <div
            key={image.key}
            className={cn(
              "min-h-[200px] max-h-[300px] bg-muted rounded-lg animate-pulse",
              imageClassName
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-3 gap-4", className)}>
      {imageUrls.map((url, index) => {
        const image = images[index];
        return (
          <div
            key={image.key}
            className={cn(
              "min-h-[200px] max-h-[300px] overflow-hidden rounded-lg cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center",
              imageClassName
            )}
            onClick={() => onImageClick?.(image.key, index)}
          >
            <BlurhashImage
              src={url}
              blurhash={image.blurhash}
              alt={image.filename || `Image ${index + 1}`}
              className="max-w-full max-h-full"
              objectFit="contain"
            />
          </div>
        );
      })}
    </div>
  );
}
